import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Container,
  CssBaseline,
  Paper,
  ThemeProvider,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet,
  ArrowBack,
  CreditCard,
  ExpandMore,
  Home,
  Inventory2,
  Login,
  NotificationsNone,
  Person,
  PersonAdd,
  ReceiptLong,
  RequestQuote,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { brand, portalTheme } from '../portalTheme';
import { BrandMark, PageTitle } from '../PortalUi';
import { LocalTestingBanner } from '../CustomerPortalLayout';
import { LanguageSwitcher, useTranslation } from '../../../../i18n';
import { currentCustomerSession } from '../../../../services/request/CustomerPortal';
import { CUSTOMER_DASHBOARD, CUSTOMER_LOGIN } from '../../../../services/constants/URIConstants';
import { ReleasepilotAffiliation } from '../../../../shared-components/branding/ReleasepilotAffiliation';

const SECTIONS = [
  { id: 'signIn', icon: <Login /> },
  { id: 'home', icon: <Home /> },
  { id: 'debits', icon: <CreditCard /> },
  { id: 'payments', icon: <ReceiptLong /> },
  { id: 'deposits', icon: <AccountBalanceWallet /> },
  { id: 'products', icon: <Inventory2 /> },
  { id: 'requestFunds', icon: <RequestQuote /> },
  { id: 'referrals', icon: <PersonAdd /> },
  { id: 'notifications', icon: <NotificationsNone /> },
  { id: 'profile', icon: <Person /> },
] as const;

const STATUSES = ['pending', 'inReview', 'approved', 'declined', 'closed'] as const;
const FAQ = ['noCode', 'signedOut', 'mismatch', 'security'] as const;

const anchorId = (id: string) => `guide-${id}`;

export function GuideContent() {
  const { t, tList } = useTranslation();

  return (
    <Box>
      <PageTitle kicker={t('guide.kicker')} title={t('guide.title')} subtitle={t('guide.subtitle')} />
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '240px 1fr' }, alignItems: 'start' }}>
        <Paper component="nav" sx={{ p: 2, position: { md: 'sticky' }, top: { md: 24 } }}>
          <Typography variant="overline" sx={{ color: brand.gold, px: 1 }}>
            {t('guide.contents')}
          </Typography>
          <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 0.25 }}>
            {SECTIONS.map(section => (
              <li key={section.id}>
                <Button
                  fullWidth
                  size="small"
                  startIcon={section.icon}
                  onClick={() => document.getElementById(anchorId(section.id))?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  sx={{ justifyContent: 'flex-start', color: brand.charcoal, fontWeight: 500, textAlign: 'left' }}
                >
                  {t(`guide.sections.${section.id}.title`)}
                </Button>
              </li>
            ))}
          </Box>
        </Paper>

        <Box sx={{ display: 'grid', gap: 2 }}>
          {SECTIONS.map((section, index) => (
            <Paper key={section.id} id={anchorId(section.id)} component="section" sx={{ p: { xs: 2.5, md: 3 }, scrollMarginTop: 24 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: brand.goldSoft,
                    color: brand.navy,
                    flexShrink: 0,
                  }}
                >
                  {section.icon}
                </Box>
                <Typography variant="h6" component="h2">
                  {index + 1}. {t(`guide.sections.${section.id}.title`)}
                </Typography>
              </Box>
              <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                {t(`guide.sections.${section.id}.intro`)}
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 3, display: 'grid', gap: 0.75 }}>
                {tList(`guide.sections.${section.id}.steps`).map(step => (
                  <Typography component="li" key={step}>
                    {step}
                  </Typography>
                ))}
              </Box>
            </Paper>
          ))}

          <Paper sx={{ p: { xs: 2.5, md: 3 } }}>
            <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
              {t('guide.statusesTitle')}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3, display: 'grid', gap: 0.75 }}>
              {STATUSES.map(status => (
                <Typography component="li" key={status}>
                  {t(`guide.statuses.${status}`)}
                </Typography>
              ))}
            </Box>
          </Paper>

          <Box>
            <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
              {t('guide.faqTitle')}
            </Typography>
            {FAQ.map(item => (
              <Accordion key={item} disableGutters sx={{ border: `1px solid ${brand.border}`, '&:before': { display: 'none' }, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ fontWeight: 600 }}>{t(`guide.faq.${item}.q`)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary">{t(`guide.faq.${item}.a`)}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          <Alert severity="info" sx={{ bgcolor: brand.goldSoft, color: brand.charcoal, '& .MuiAlert-icon': { color: brand.warning } }}>
            {t('guide.disclaimer')}
          </Alert>
        </Box>
      </Box>
    </Box>
  );
}

/** Public copy of the guide, readable before signing in. */
export default function PublicGuidePage() {
  const { t } = useTranslation();
  const signedIn = Boolean(currentCustomerSession());

  return (
    <ThemeProvider theme={portalTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: brand.ivory, display: 'flex', flexDirection: 'column' }}>
        <LocalTestingBanner />
        <Box component="header" sx={{ bgcolor: brand.navy, color: brand.ivory }}>
          <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, gap: 2 }}>
            <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'inherit', textDecoration: 'none' }}>
              <BrandMark size={42} />
              <Box>
                <Typography variant="overline" sx={{ color: brand.gold, lineHeight: 1.2, display: 'block' }}>
                  {t('portal.layout.brand')}
                </Typography>
                <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>{t('portal.layout.product')}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LanguageSwitcher />
              <Button
                component={RouterLink}
                to={signedIn ? CUSTOMER_DASHBOARD : CUSTOMER_LOGIN}
                variant="outlined"
                size="small"
                startIcon={<ArrowBack />}
                sx={{ color: brand.ivory, borderColor: 'rgba(249,248,245,0.4)', '&:hover': { borderColor: brand.gold }, whiteSpace: 'nowrap' }}
              >
                {signedIn ? t('guide.openPortal') : t('guide.backToSignIn')}
              </Button>
            </Box>
          </Container>
        </Box>
        <Container component="main" maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, flex: 1 }}>
          <GuideContent />
        </Container>
        <Box component="footer" sx={{ borderTop: `1px solid ${brand.border}`, py: 2 }}>
          <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center' }}>
            <ReleasepilotAffiliation compact />
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
